import { type ContactDetail, type JobListItem, PAYMENT_METHODS, RECEIPT_STATUS_LABELS, type ReceiptItem, type ReceiptLine, type ReceiptMatch, SALES_TAX, type VendorSuggestions, dueFromTerms, lineCategories, pickPaidFrom } from "@nmt/shared";
import { PaidBadge } from "../components/paid-badge";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, del, errorMessage, get, patch, post } from "../api";
import { ContactPicker } from "../components/forms";
import { Alert, Badge, Button, Checkbox, Field, Loading, SelectField, TextAreaField, cx, formatCents, formatDate, parseDollars } from "../components/ui";
import { useSession } from "../session";

const dollars = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2));
/** Typed dollars → cents; a minus or parentheses makes a discount line ("-4.72", "(4.72)"). */
const cents = (v: string) => {
  const t = (v ?? "").trim();
  const minus = /^-|^\(.*\)$/.test(t);
  const c = parseDollars(t.replace(/^-|^\(|\)$/g, ""));
  return c === null || Number.isNaN(c) ? null : minus ? -c : c;
};

/** A line being edited: the amount as typed. */
type Row = { key: number; category: string; description: string; amount: string; jobId: string | null };
let nextKey = 1;
const toRow = (l: ReceiptLine): Row => ({ key: nextKey++, category: l.category, description: l.description, amount: dollars(l.amountCents), jobId: l.jobId });

interface Form {
  kind: "bill" | "expense" | "credit";
  vendor: { id: string; name: string } | null;
  vendorName: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  terms: string;
  paymentMethod: string;
  paidFrom: string;
  cardLast4: string;
  total: string;
  memo: string;
  jobId: string | null;
  noJobReason: string;
  earlyQbReason: string;
  takeDiscount: boolean;
}

/**
 * A receipt or bill, full page (like QuickBooks' expense and bill screens): the document on the
 * left; on the right who it's from, dates and terms, the line items (category, description, job,
 * amount; sales tax is a line too), and the total they have to add up to.
 */
export function ReceiptPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useSession();
  const q = useQuery({ queryKey: ["receipt", id], queryFn: () => get<ReceiptItem>(`/api/receipts/${id}`), refetchInterval: (x) => (x.state.data?.status === "reading" ? 1500 : false) });
  const r = q.data;
  const [form, setForm] = useState<Form | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [breakdown, setBreakdown] = useState(true);
  const [reason, setReason] = useState("");
  const [needReason, setNeedReason] = useState<string[] | null>(null);
  const [backNote, setBackNote] = useState("");
  const [matching, setMatching] = useState(false);

  // Fill the form from what was read (again when a read finishes).
  useEffect(() => {
    if (!r || r.status === "reading") return;
    setForm({
      kind: r.kind,
      vendor: r.vendorContact ? { id: r.vendorContact.id, name: r.vendorContact.name } : null,
      vendorName: r.vendorName,
      invoiceNumber: r.invoiceNumber,
      date: r.date ?? "",
      dueDate: r.dueDate ?? "",
      terms: r.terms,
      paymentMethod: r.paymentMethod,
      paidFrom: r.paidFromAccountId ?? "",
      cardLast4: r.cardLast4,
      total: dollars(r.totalCents),
      memo: r.memo,
      jobId: r.jobId,
      noJobReason: r.noJobReason,
      earlyQbReason: r.earlyQbReason,
      takeDiscount: r.takeDiscount,
    });
    // A credit's lines are stored negative; they're shown and typed as how much is credited.
    setRows(r.lines.map((l) => toRow(r.kind === "credit" ? { ...l, amountCents: -l.amountCents } : l)));
    setLabels((l) => ({ ...l, ...r.jobLabels, ...(r.jobId && r.jobLabel ? { [r.jobId]: r.jobLabel } : {}) }));
  }, [r?.id, r?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/receipts"));
  const done = async (x: ReceiptItem) => {
    qc.setQueryData(["receipt", id], x);
    await qc.invalidateQueries({ queryKey: ["receipts"] });
  };
  const body = () => {
    const f = form!;
    return {
      kind: f.kind,
      takeDiscount: f.takeDiscount,
      vendorContactId: f.vendor?.id ?? null,
      vendorName: f.vendor?.name ?? f.vendorName,
      invoiceNumber: f.invoiceNumber,
      date: f.date || null,
      dueDate: f.kind === "bill" ? f.dueDate || null : null,
      terms: f.kind === "bill" ? f.terms : "",
      paymentMethod: f.kind === "expense" ? f.paymentMethod : "",
      paidFromAccountId: f.kind === "expense" ? f.paidFrom || null : null,
      totalCents: cents(f.total),
      memo: f.memo,
      jobId: f.jobId,
      noJobReason: rows.some((x) => !x.jobId) ? f.noJobReason : "",
      earlyQbReason: f.earlyQbReason,
      lines: rows.map((x) => ({ category: x.category, description: x.description, amountCents: (f.kind === "credit" ? -1 : 1) * (cents(x.amount) ?? 0), jobId: x.jobId })),
    };
  };
  const save = useMutation({ mutationFn: () => patch<ReceiptItem>(`/api/receipts/${id}`, body()), onSuccess: done });
  const ready = useMutation({
    mutationFn: async () => {
      await patch<ReceiptItem>(`/api/receipts/${id}`, body());
      return post<ReceiptItem>(`/api/receipts/${id}/ready`, needReason ? { overrideReason: reason } : {});
    },
    onSuccess: done,
    onError: (e) => {
      // The lines were saved; refresh so a job that isn't in QuickBooks yet shows its notice.
      void qc.invalidateQueries({ queryKey: ["receipt", id] });
      if (e instanceof ApiError && e.code === "compliance") setNeedReason((e.details as { problems: string[] }).problems);
    },
  });
  const act = useMutation({ mutationFn: (what: "approve" | "reopen" | "read-again") => post<ReceiptItem>(`/api/receipts/${id}/${what}`, what === "reopen" ? { note: backNote } : {}), onSuccess: done });
  const remove = useMutation({
    mutationFn: () => del(`/api/receipts/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["receipts"] });
      back();
    },
  });

  if (!r || (!form && r.status !== "reading")) return <Loading />;
  const editable = r.status === "draft" || r.status === "read_failed";
  const categories = lineCategories(me?.lists.expenseCategories ?? []);
  const err = save.error ?? (ready.error instanceof ApiError && ready.error.code === "compliance" ? null : ready.error) ?? act.error ?? remove.error;
  const set = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f));

  // The money, as typed: lines, the tax lines, and how far they are from the receipt's total.
  const amounts = rows.map((x) => cents(x.amount) ?? 0);
  const linesSum = amounts.reduce((s, a) => s + a, 0);
  const tax = rows.reduce((s, x, i) => s + (x.category === SALES_TAX ? amounts[i]! : 0), 0);
  const total = form ? cents(form.total) : null;
  const off = total === null ? null : total - linesSum;
  const notInQb = (r?.jobsNotInQuickBooks ?? []).filter((j) => rows.some((x) => x.jobId === j.id));
  const setRow = (key: number, p: Partial<Row>) => setRows((rs) => rs.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const label = (jobId: string | null) => (jobId ? (labels[jobId] ?? "Job") : "No job");

  return (
    <div className="flex flex-col gap-4 pb-24">
      {/* What it is, and the amount, at a glance. */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="min-w-0 flex-1">
          <button onClick={back} className="text-sm text-muted hover:text-brand">
            ← Back
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-2xl font-bold">{form?.vendor?.name || form?.vendorName || r.vendorName || "Receipt"}</h1>
            <Badge tone={r.status === "approved" ? "ok" : r.status === "ready" ? "brand" : "wait"}>{RECEIPT_STATUS_LABELS[r.status]}</Badge>
            {r.inQuickBooks && <Badge tone="ok">In QuickBooks</Badge>}
            <PaidBadge r={r} kind={form?.kind ?? r.kind} withDate />
          </div>
          <div className="text-sm text-muted">
            {(form?.kind ?? r.kind) === "credit" ? "Vendor credit" : (form?.kind ?? r.kind) === "bill" ? "Bill" : "Expense"}
            {form?.invoiceNumber ? ` #${form.invoiceNumber}` : ""}
            {r.submittedBy && ` · turned in by ${r.submittedBy.name}`}
            {r.approvedBy && ` · approved by ${r.approvedBy.name}`}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">{(form?.kind ?? r.kind) === "credit" ? "Credit" : (form?.kind ?? r.kind) === "bill" && !r.qbPaidAt ? "Balance due" : "Total"}</div>
          <div className="text-3xl font-bold">{total !== null ? formatCents(total) : r.totalCents !== null ? formatCents(r.totalCents) : "—"}</div>
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex flex-col gap-2 lg:sticky lg:top-4">
          <DocumentViewer file={r.file} />
          {r.splitFrom && (
            <p className="text-sm text-muted">
              Split from{" "}
              <a href={r.splitFrom.url} target="_blank" rel="noreferrer" className="font-semibold underline">
                {r.splitFrom.name}
              </a>
              , {r.splitFrom.pages}.
            </p>
          )}
          {editable && r.source !== "quickbooks" && r.file && (
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                className="text-sm font-semibold text-brand hover:underline"
                onClick={() => window.confirm("Read it again? What's filled in now is replaced by a fresh reading (a PDF with several invoices is split).") && act.mutate("read-again")}
              >
                Read again
              </button>
              {r.file.contentType === "application/pdf" && <SplitButton id={id} onSplit={() => qc.invalidateQueries({ queryKey: ["receipts"] }).then(() => q.refetch())} />}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {r.status === "reading" && <Alert tone="info">Reading the receipt…</Alert>}
          {r.readError && r.status === "read_failed" && (
            <Alert>
              {r.readError}{" "}
              <button className="font-semibold underline" onClick={() => act.mutate("read-again")}>
                Try again
              </button>
            </Alert>
          )}
          {err && <Alert>{errorMessage(err)}</Alert>}
          {r.source === "quickbooks" ? (
            <Alert tone="info">Entered in QuickBooks{r.qbType ? ` (${r.qbType === "Purchase" ? "expense" : r.qbType === "VendorCredit" ? "vendor credit" : "bill"})` : ""} and read from there each morning. Change it in QuickBooks.{r.kind === "credit" ? " It's a credit: its amounts come off the job's costs." : ""}</Alert>
          ) : (
            r.status === "approved" && <Alert tone="ok">Approved. {r.inQuickBooks ? "It's in QuickBooks." : "It goes to QuickBooks once QuickBooks is connected."}</Alert>
          )}
          {r.workOrder && <p className="text-sm text-ink-soft">Drafted when work order {r.workOrder.number} was marked done, for its amount. If the sub sends their own invoice, check it against this and keep one.</p>}
          {r.complianceOverride && (
            <p className="text-sm text-muted">
              Marked ready without complete paperwork by {r.complianceOverride.by}: "{r.complianceOverride.reason}"
            </p>
          )}

          {form && (
            <fieldset disabled={!editable} className="flex flex-col gap-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <span className="mb-1 block text-sm font-semibold text-ink-soft">Type</span>
                  <div className="inline-flex rounded-[var(--radius-control)] bg-mist p-1">
                    {(["expense", "bill", "credit"] as const).map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => set({ kind: k, ...(k === "bill" && !form.dueDate ? { dueDate: dueFromTerms(form.terms || r.vendorTerms, form.date) ?? new Date().toLocaleDateString("en-CA") } : {}) })}
                        className={cx("rounded-[6px] px-3 py-1.5 text-sm font-semibold", form.kind === k ? "bg-white shadow-sm" : "text-muted hover:text-ink")}
                      >
                        {k === "expense" ? "Expense (paid)" : k === "bill" ? "Bill (to pay)" : "Vendor credit"}
                      </button>
                    ))}
                  </div>
                </div>
                {editable && (
                  <button type="button" className="text-sm font-semibold text-brand hover:underline" onClick={() => setMatching((m) => !m)}>
                    Search for a match
                  </button>
                )}
              </div>
              {matching && <Matches id={id} onLinked={done} />}

              {r.source === "quickbooks" ? (
                <div>
                  <div className="mb-1 text-sm font-semibold text-ink-soft">Vendor</div>
                  {r.vendorContact ? (
                    <Link to={`/contacts/${r.vendorContact.id}`} className="font-semibold hover:text-brand">
                      {r.vendorContact.name}
                    </Link>
                  ) : (
                    <p className="text-sm">
                      <span className="font-semibold">{r.vendorName || "No vendor"}</span>
                      {r.vendorName && <span className="text-muted">: the QuickBooks vendor, not linked to a contact yet. On their contact page (add them as a sub or vendor if they aren't one), use “Link to a QuickBooks vendor”; the next sync fills it in.</span>}
                    </p>
                  )}
                </div>
              ) : (
                <VendorBox receiptId={r.id} vendor={form.vendor} vendorName={form.vendorName} vendorTerms={r.vendorTerms} onPick={(v) => set({ vendor: v, ...(v ? { vendorName: v.name } : {}) })} onName={(vendorName) => set({ vendorName })} />
              )}

              {form.kind === "bill" ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field
                    label="Terms"
                    value={form.terms}
                    placeholder={r.vendorTerms || "Due on receipt"}
                    onChange={(e) => {
                      const terms = e.target.value;
                      set({ terms, ...(dueFromTerms(terms, form.date) ? { dueDate: dueFromTerms(terms, form.date)! } : {}) });
                    }}
                    hint="Net 30, Due on receipt, 2% 10th Net 11th…"
                  />
                  <Field label="Bill date" type="date" value={form.date} onChange={(e) => set({ date: e.target.value, ...(dueFromTerms(form.terms, e.target.value) ? { dueDate: dueFromTerms(form.terms, e.target.value)! } : {}) })} />
                  <Field label="Due date" type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
                  {r.earlyDiscountCents !== null && r.earlyDiscountBy && (
                    <div className="sm:col-span-3">
                      <Checkbox
                        label={`Take the early-pay discount: ${formatCents(r.earlyDiscountCents)} off if paid by ${formatDate(r.earlyDiscountBy)}`}
                        description={
                          r.discountLapsedAt
                            ? "Missed: it wasn't paid in time, so the full bill is owed."
                            : form.takeDiscount
                              ? `Approving also makes a ${formatCents(r.earlyDiscountCents)} vendor credit to the discounts account (not on the job), so ${formatCents((cents(form.total) ?? 0) - r.earlyDiscountCents)} is left to pay. Due by the discount's last day.`
                              : "Not taken: the full bill is owed by its due date."
                        }
                        checked={form.takeDiscount}
                        onChange={(takeDiscount) => set({ takeDiscount, dueDate: takeDiscount ? r.earlyDiscountBy! : (dueFromTerms(form.terms || r.vendorTerms, form.date) ?? form.dueDate) })}
                      />
                    </div>
                  )}
                </div>
              ) : form.kind === "credit" ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Credit date" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} hint="Approving it makes a vendor credit in QuickBooks, used on their next bill." />
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Payment date" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
                  <SelectField
                    label="Paid with"
                    value={form.paymentMethod}
                    onChange={(e) => {
                      // A different way of paying: pick the account again (card digits first).
                      const paymentMethod = e.target.value;
                      const picked = pickPaidFrom({ kind: "expense", paymentMethod, cardLast4: form.cardLast4 }, me?.paymentAccounts ?? [], me?.paymentAccounts.find((a) => a.isDefault)?.accountId ?? null);
                      set({ paymentMethod, paidFrom: picked ?? "" });
                    }}
                  >
                    <option value="">—</option>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </SelectField>
                  {(() => {
                    // A personal payment has no default: pick the account it's reimbursed from.
                    const personal = form.paymentMethod === "Personal (reimburse)";
                    const def = personal ? undefined : me?.paymentAccounts.find((a) => a.isDefault);
                    return (
                      <SelectField
                        label="Paid from"
                        value={form.paidFrom}
                        onChange={(e) => set({ paidFrom: e.target.value })}
                        hint={
                          !me?.paymentAccounts.length
                            ? "Add bank and card accounts in Settings → QuickBooks."
                            : personal
                              ? "The account it's paid back from (add one in Settings → QuickBooks if it's missing)."
                              : form.cardLast4
                                ? `Card ending in ${form.cardLast4}`
                                : undefined
                        }
                      >
                        <option value="">{def ? `${def.name} (default)` : "Pick the account"}</option>
                        {(me?.paymentAccounts ?? [])
                          .filter((a) => a.accountId !== def?.accountId)
                          .map((a) => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.name}
                              {a.last4 ? ` ····${a.last4}` : ""}
                            </option>
                          ))}
                      </SelectField>
                    );
                  })()}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <Field label={form.kind === "bill" ? "Bill no." : form.kind === "credit" ? "Credit memo no." : "Ref no."} value={form.invoiceNumber} onChange={(e) => set({ invoiceNumber: e.target.value })} />
                <div className="sm:col-span-2">
                  <JobPicker
                    label="Job"
                    value={form.jobId}
                    labelOf={label}
                    onChange={(jobId, name) => {
                      if (jobId && name) setLabels((l) => ({ ...l, [jobId]: name }));
                      // Lines that were on the old job move with it.
                      setRows((rs) => rs.map((x) => (x.jobId === form.jobId ? { ...x, jobId } : x)));
                      set({ jobId });
                    }}
                    hint="Every line starts on this job; change a line's job below."
                  />
                  {!form.jobId && r && r.status !== "reading" && (
                    <JobSuggestions
                      id={id}
                      onPick={(jobId, name) => {
                        setLabels((l) => ({ ...l, [jobId]: name }));
                        setRows((rs) => rs.map((x) => (x.jobId === null ? { ...x, jobId } : x)));
                        set({ jobId });
                      }}
                    />
                  )}
                </div>
              </div>

              {/* The line items. */}
              <div className="rounded-[var(--radius-panel)] border border-line">
                <div className="flex items-center justify-between gap-3 border-b border-mist px-4 py-2.5">
                  <span className="font-semibold">Category details</span>
                  <Checkbox label="Show breakdown" checked={breakdown} onChange={setBreakdown} />
                </div>
                {breakdown ? (
                  <div className="flex flex-col">
                    {rows.map((x, i) => (
                      <div key={x.key} className="grid grid-cols-[1.5rem_minmax(0,1fr)_7rem_1.75rem] items-center gap-2 border-b border-mist px-3 py-2.5 sm:grid-cols-[1.5rem_minmax(0,1fr)_minmax(0,1fr)_7rem_1.75rem]">
                        <span className="text-sm text-muted">{i + 1}</span>
                        <select
                          aria-label={`Line ${i + 1} category`}
                          value={x.category}
                          onChange={(e) => setRow(x.key, { category: e.target.value })}
                          className={cx("min-w-0 rounded-[var(--radius-control)] border border-line bg-white px-2 py-2 text-sm", !x.category && "border-brand")}
                        >
                          <option value="">Category</option>
                          {[...new Set([...categories, ...(x.category ? [x.category] : [])])].map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                        <div className="col-start-2 col-end-4 row-start-3 min-w-0 sm:col-start-3 sm:col-end-4 sm:row-start-1">
                          <JobPicker compact value={x.jobId} labelOf={label} onChange={(jobId, name) => (jobId && name && setLabels((l) => ({ ...l, [jobId]: name })), setRow(x.key, { jobId }))} />
                        </div>
                        <input
                          aria-label={`Line ${i + 1} amount`}
                          inputMode="decimal"
                          value={x.amount}
                          onChange={(e) => setRow(x.key, { amount: e.target.value })}
                          placeholder="0.00"
                          className="min-w-0 rounded-[var(--radius-control)] border border-line bg-white px-2 py-2 text-right text-sm sm:col-start-4"
                        />
                        <button type="button" aria-label={`Remove line ${i + 1}`} className="text-lg text-muted hover:text-danger sm:col-start-5" onClick={() => setRows((rs) => rs.filter((y) => y.key !== x.key))}>
                          ×
                        </button>
                        <input
                          aria-label={`Line ${i + 1} description`}
                          value={x.description}
                          onChange={(e) => setRow(x.key, { description: e.target.value })}
                          placeholder="Description"
                          className="col-start-2 col-end-4 row-start-2 min-w-0 rounded-[var(--radius-control)] border border-line bg-white px-2 py-2 text-sm sm:col-end-5"
                        />
                      </div>
                    ))}
                    {editable && (
                      <div className="flex flex-wrap gap-3 px-4 py-2.5">
                        <button
                          type="button"
                          className="text-sm font-semibold text-brand hover:underline"
                          onClick={() => setRows((rs) => [...rs, { key: nextKey++, category: "", description: "", amount: off && off > 0 ? dollars(off) : "", jobId: form.jobId }])}
                        >
                          + Add line
                        </button>
                        {!rows.some((x) => x.category === SALES_TAX) && (
                          <button
                            type="button"
                            className="text-sm font-semibold text-brand hover:underline"
                            onClick={() => setRows((rs) => [...rs, { key: nextKey++, category: SALES_TAX, description: "Sales tax", amount: off && off > 0 ? dollars(off) : "", jobId: form.jobId }])}
                          >
                            + Sales tax
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="px-4 py-3 text-sm text-ink-soft">
                    {rows.length} line{rows.length === 1 ? "" : "s"}: {[...new Set(rows.map((x) => x.category).filter(Boolean))].join(", ") || "no category yet"}.
                  </p>
                )}
                <div className="flex flex-col items-end gap-1 border-t border-mist px-4 py-3 text-sm">
                  <div className="flex gap-6">
                    <span className="text-muted">Subtotal</span>
                    <span className="w-28 text-right">{formatCents(linesSum - tax)}</span>
                  </div>
                  <div className="flex gap-6">
                    <span className="text-muted">Sales tax</span>
                    <span className="w-28 text-right">{formatCents(tax)}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="font-semibold">{form.kind === "credit" ? "Total credited" : "Total on the receipt"}</span>
                    <input aria-label="Total" inputMode="decimal" value={form.total} onChange={(e) => set({ total: e.target.value })} className="w-28 rounded-[var(--radius-control)] border border-line px-2 py-1.5 text-right font-semibold" />
                  </div>
                  {off !== null && off !== 0 && (
                    <p className="font-semibold text-danger">
                      The lines are {formatCents(Math.abs(off))} {off > 0 ? "short of" : "over"} the total.
                    </p>
                  )}
                </div>
              </div>

              {rows.some((x) => !x.jobId) && (
                <Field
                  label="Why no job?"
                  value={form.noJobReason}
                  onChange={(e) => set({ noJobReason: e.target.value })}
                  placeholder="Shop tools, office supplies, truck fuel…"
                  hint="A line without a job is overhead: say what it's for. It goes on the QuickBooks memo."
                />
              )}

              {notInQb.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Alert tone="info">
                    {notInQb.map((j) => j.label).join(", ")} {notInQb.length > 1 ? "aren't" : "isn't"} in QuickBooks yet (it hasn't reached Sold). Approving this creates {notInQb.length > 1 ? "them" : "it"} there; the job's stage doesn't change.
                  </Alert>
                  <Field
                    label="Why does it need to be in QuickBooks now?"
                    value={form.earlyQbReason}
                    onChange={(e) => set({ earlyQbReason: e.target.value })}
                    placeholder="Deposit on materials before the contract is signed…"
                    hint="Required. It's logged and goes on the QuickBooks memo."
                  />
                </div>
              )}

              <TextAreaField label="Memo" value={form.memo} onChange={(e) => set({ memo: e.target.value })} rows={2} />
            </fieldset>
          )}

          {r.warnings
            .filter((w) => !w.startsWith("The lines add up to"))
            .map((w) => (
              <Alert key={w} tone="info">
                {w}
              </Alert>
            ))}
          {!!r.matchingWorkOrders.length && !r.workOrder && (
            <div className="rounded-[var(--radius-control)] bg-mist px-3 py-2 text-sm">
              <div className="mb-1 font-semibold">This sub's work orders on the job</div>
              {r.matchingWorkOrders.map((w) => (
                <div key={w.id} className="flex justify-between gap-3">
                  <Link to={`/jobs/${r.jobId}`} className="hover:text-brand">
                    Work order {w.number}
                    {w.title && ` · ${w.title}`} <span className="text-muted">({w.status})</span>
                  </Link>
                  <span>{formatCents(w.totalCents)}</span>
                </div>
              ))}
            </div>
          )}
          {needReason && editable && (
            <>
              <Alert>Their paperwork isn't complete: {needReason.join("; ")}.</Alert>
              <TextAreaField label="Why mark it ready anyway? (saved in the activity log)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
            </>
          )}
          {(r.status === "ready" || r.status === "approved") && can("receipts.approve") && !r.inQuickBooks && r.source !== "quickbooks" && (
            <div className="flex flex-wrap items-end gap-2 border-t border-mist pt-3">
              <div className="min-w-0 flex-1">
                <Field label="Send back with a note (optional)" value={backNote} onChange={(e) => setBackNote(e.target.value)} />
              </div>
              <Button busy={act.isPending && act.variables === "reopen"} onClick={() => act.mutate("reopen")}>
                Send back
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* The buttons stay in reach. */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white/95 backdrop-blur lg:left-[232px]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3 sm:px-8">
          <Button variant="quiet" onClick={back}>
            Cancel
          </Button>
          {editable && (r.submittedBy?.id === me?.user.id || can("receipts.approve")) && (
            <Button variant="quiet" className="text-danger" busy={remove.isPending} onClick={() => window.confirm("Delete this receipt? The file stays in the files.") && remove.mutate()}>
              Delete
            </Button>
          )}
          {!editable && (r.status === "approved" || r.source === "quickbooks") && can("receipts.approve") && (
            <Button
              variant="quiet"
              className="text-danger"
              busy={remove.isPending}
              onClick={() => window.confirm(r.qbType ? "Delete this receipt here and in QuickBooks? (While QuickBooks writes are off, the QuickBooks side is only recorded as a dry run.)" : "Delete this approved receipt?") && remove.mutate()}
            >
              {r.qbType ? "Delete here and in QuickBooks" : "Delete"}
            </Button>
          )}
          <span className="flex-1" />
          {editable && (
            <>
              <Button busy={save.isPending} onClick={() => save.mutate()}>
                Save draft
              </Button>
              <Button variant="primary" busy={ready.isPending} disabled={!!needReason && reason.trim().length < 3} onClick={() => ready.mutate()}>
                {needReason ? "Mark ready anyway" : "Mark ready"}
              </Button>
            </>
          )}
          {r.status === "ready" && can("receipts.approve") && (
            <Button variant="primary" busy={act.isPending && act.variables === "approve"} onClick={() => act.mutate("approve")}>
              Approve
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** The receipt itself, with zoom, rotate, and download (a PDF uses the browser's viewer). */
function DocumentViewer({ file }: { file: ReceiptItem["file"] }) {
  const [zoom, setZoom] = useState(1);
  const [turn, setTurn] = useState(0);
  if (!file) return <div className="rounded-[var(--radius-panel)] border border-line p-6 text-center text-muted">No file.</div>;
  const pdf = file.contentType === "application/pdf";
  const tool = "rounded-[var(--radius-control)] px-2.5 py-1.5 text-sm font-semibold text-white/85 hover:bg-white/10 hover:text-white";
  return (
    <div className="overflow-hidden rounded-[var(--radius-panel)] border border-line bg-[#3a3a3a]">
      <div className="flex items-center gap-1 bg-[#222] px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{file.name}</span>
        {!pdf && (
          <>
            <button type="button" className={tool} aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>
              −
            </button>
            <button type="button" className={tool} aria-label="Fit" onClick={() => setZoom(1)}>
              Fit
            </button>
            <button type="button" className={tool} aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(4, z + 0.25))}>
              +
            </button>
            <button type="button" className={tool} aria-label="Rotate" onClick={() => setTurn((t) => (t + 90) % 360)}>
              ⟳
            </button>
          </>
        )}
        <a href={file.url} target="_blank" rel="noreferrer" className={tool} aria-label="Download">
          ⤓
        </a>
      </div>
      {pdf ? (
        <iframe src={file.url} title="Receipt" className="h-[75dvh] w-full bg-white" />
      ) : (
        <div className="h-[75dvh] overflow-auto">
          <img src={file.previewUrl} alt="Receipt" style={{ width: `${zoom * 100}%`, transform: `rotate(${turn}deg)`, transformOrigin: "center" }} className="mx-auto max-w-none transition-transform" />
        </div>
      )}
    </div>
  );
}

/** Who it's from: a vendor or sub in contacts (with their details), or the name as printed. */
function VendorBox({ receiptId, vendor, vendorName, vendorTerms, onPick, onName }: { receiptId: string; vendor: { id: string; name: string } | null; vendorName: string; vendorTerms: string; onPick: (v: { id: string; name: string } | null) => void; onName: (n: string) => void }) {
  const { can } = useSession();
  const detail = useQuery({ queryKey: ["contact", vendor?.id], queryFn: () => get<ContactDetail>(`/api/contacts/${vendor!.id}`), enabled: !!vendor });
  // No vendor yet: who it could be (contacts it might be, or a QuickBooks vendor by that name).
  const ideas = useQuery({ queryKey: ["receipt-vendor-ideas", receiptId], queryFn: () => get<VendorSuggestions>(`/api/receipts/${receiptId}/vendor-suggestions`), enabled: !vendor, staleTime: 60_000 });
  const fromQb = useMutation({
    mutationFn: (qbId: string) => post<{ added: { id: string; name: string }[] }>("/api/integrations/quickbooks/vendors/import", { qbIds: [qbId] }),
    onSuccess: (res) => res.added[0] && onPick({ id: res.added[0].id, name: res.added[0].name }),
  });
  const d = detail.data;
  const tip = !vendor && ideas.data;
  return (
    <div className="flex flex-col gap-2">
      <ContactPicker label="Vendor" value={vendor} onChange={onPick} hint={vendor ? undefined : "Find the supplier or sub in contacts."} />
      {tip && (tip.contacts.length > 0 || tip.quickbooks) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted">{tip.contacts.length ? "Is it" : "In QuickBooks:"}</span>
          {tip.contacts.map((c) => (
            <button key={c.id} type="button" className="rounded-full bg-mist px-3 py-1 font-semibold text-ink-soft hover:bg-brand hover:text-white" onClick={() => onPick(c)}>
              {c.name}
            </button>
          ))}
          {tip.quickbooks && can("customers.edit") && (
            <Button className="px-3 py-1" busy={fromQb.isPending} onClick={() => fromQb.mutate(tip.quickbooks!.qbId)}>
              Add {tip.quickbooks.name} from QuickBooks
            </Button>
          )}
          {fromQb.error && <span className="text-danger">{errorMessage(fromQb.error)}</span>}
        </div>
      )}
      {!vendor && <Field label="Name as printed" value={vendorName} onChange={(e) => onName(e.target.value)} hint="Not in contacts yet: kept as written." />}
      {vendor && d && (
        <div className="grid gap-x-6 gap-y-1 rounded-[var(--radius-control)] bg-mist/70 px-4 py-3 text-sm sm:grid-cols-2">
          <div>
            <Link to={`/contacts/${d.id}`} className="font-semibold hover:text-brand">
              {d.displayName}
            </Link>
            <div className="text-muted">{d.typeName}</div>
          </div>
          <div className="text-ink-soft">
            {[d.street, [d.city, d.state].filter(Boolean).join(", "), d.zip].filter(Boolean).join(" ")}
            {d.phone && <div>{d.phone}</div>}
            <div>{vendorTerms || d.paymentTerms ? `Terms: ${vendorTerms || d.paymentTerms}` : "No payment terms on file"}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Picks a job by searching (customer, number, address). */
/** Jobs the receipt might be for (from its PO, ship-to, or name), when none was clear enough to pick. */
function JobSuggestions({ id, onPick }: { id: string; onPick: (jobId: string, name: string) => void }) {
  const q = useQuery({ queryKey: ["receipt-job-suggestions", id], queryFn: () => get<{ id: string; label: string; why: string }[]>(`/api/receipts/${id}/job-suggestions`) });
  if (!q.data?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-muted">Maybe:</span>
      {q.data.slice(0, 3).map((s) => (
        <button key={s.id} type="button" title={`Matched on ${s.why}`} onClick={() => onPick(s.id, s.label)} className="rounded-full bg-brand-wash px-2.5 py-1 font-semibold text-brand-deep hover:bg-brand hover:text-white">
          {s.label}
        </button>
      ))}
    </div>
  );
}

function JobPicker({ label, value, labelOf, onChange, hint, compact }: { label?: string; value: string | null; labelOf: (id: string | null) => string; onChange: (jobId: string | null, name?: string) => void; hint?: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const results = useQuery({ queryKey: ["job-search", q], queryFn: () => get<{ items: JobListItem[] }>(`/api/jobs?status=all&limit=8&q=${encodeURIComponent(q)}`), enabled: open && q.trim().length >= 2 });
  useEffect(() => {
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={box} className="relative">
      {label && <span className="mb-1 block text-sm font-semibold text-ink-soft">{label}</span>}
      {open ? (
        <input autoFocus aria-label={label ?? "Job"} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer, job #, address" className={cx("w-full rounded-[var(--radius-control)] border border-brand px-2", compact ? "py-2 text-sm" : "py-2")} />
      ) : (
        <button type="button" onClick={() => setOpen(true)} className={cx("w-full truncate rounded-[var(--radius-control)] border border-line bg-white px-2 text-left", compact ? "py-2 text-sm" : "py-2", !value && "text-muted")}>
          {labelOf(value)}
        </button>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full min-w-64 overflow-auto rounded-[var(--radius-control)] border border-line bg-white shadow-lg">
          {results.data?.items.map((j) => (
            <li key={j.id}>
              <button type="button" className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-mist" onClick={() => (onChange(j.id, `#${j.number} · ${j.customerName}${j.title ? ` · ${j.title}` : ""}`), setOpen(false), setQ(""))}>
                <span className="font-semibold">
                  #{j.number} · {j.customerName}
                  {j.title && <span className="font-normal text-ink-soft"> · {j.title}</span>}
                </span>
                <span className="text-xs text-muted">{[j.street, j.city, j.workflowName, j.stageName].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
          <li>
            <button type="button" className="w-full px-3 py-2 text-left text-sm text-muted hover:bg-mist" onClick={() => (onChange(null), setOpen(false), setQ(""))}>
              No job (overhead)
            </button>
          </li>
        </ul>
      )}
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** Search for a match: another receipt it duplicates, or a bill already in QuickBooks. */
function Matches({ id, onLinked }: { id: string; onLinked: (r: ReceiptItem) => void }) {
  const m = useQuery({ queryKey: ["receipt-matches", id], queryFn: () => get<ReceiptMatch[]>(`/api/receipts/${id}/matches`) });
  const link = useMutation({ mutationFn: (qbId: string) => post<ReceiptItem>(`/api/receipts/${id}/link-quickbooks`, { qbId }), onSuccess: onLinked });
  return (
    <div className="rounded-[var(--radius-control)] border border-line px-4 py-3 text-sm">
      {link.error && <Alert>{errorMessage(link.error)}</Alert>}
      {m.isLoading ? (
        <Loading />
      ) : !m.data?.length ? (
        <p className="text-muted">No match: nothing else from this vendor with the same number or amount (here or in QuickBooks).</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {m.data.map((x) => (
            <li key={`${x.kind}${x.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-semibold">{x.label}</span>
              <span className="text-muted">{[x.date ? formatDate(x.date) : null, x.totalCents !== null ? formatCents(x.totalCents) : null, x.why].filter(Boolean).join(" · ")}</span>
              <span className="flex-1" />
              {x.kind === "receipt" ? (
                <Link to={`/receipts/${x.id}`} className="font-semibold text-brand hover:underline">
                  Open it
                </Link>
              ) : (
                <Button className="px-3 py-1" busy={link.isPending} onClick={() => link.mutate(x.id)}>
                  It's this one: link it
                </Button>
              )}
            </li>
          ))}
          <li className="text-xs text-muted">A duplicate here? Open the other one, keep one, and delete the extra.</li>
        </ul>
      )}
    </div>
  );
}

/** Splits a PDF holding several invoices by hand: page ranges, one receipt each. */
function SplitButton({ id, onSplit }: { id: string; onSplit: () => void }) {
  const [open, setOpen] = useState(false);
  const [ranges, setRanges] = useState("");
  const split = useMutation({ mutationFn: () => post<{ ids: string[] }>(`/api/receipts/${id}/split`, { ranges }), onSuccess: () => (setOpen(false), onSplit()) });
  if (!open)
    return (
      <button type="button" className="self-start text-sm font-semibold text-brand hover:underline" onClick={() => setOpen(true)}>
        Split…
      </button>
    );
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3 text-sm">
      <p>One file, several invoices? Give each invoice's pages, in order, like <b>1-3, 4</b>. Each part becomes its own PDF and receipt, read again; the original file stays.</p>
      {split.error && <Alert>{errorMessage(split.error)}</Alert>}
      <div className="flex flex-wrap gap-2">
        <input aria-label="Pages for each invoice" value={ranges} onChange={(e) => setRanges(e.target.value)} placeholder="1-3, 4" className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line px-2 py-1.5" />
        <Button variant="primary" busy={split.isPending} disabled={!ranges.trim()} onClick={() => split.mutate()}>
          Split
        </Button>
        <Button variant="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
